// Operatiivinen ohjaus — a 15-min-cadence view of the same loop the Pulssi header already
// summarizes (see OptimizationBuildingSection): ulkoiset vaikuttimet → tilan tarve → resepti →
// yhteisvaikutus. Real data comes from one call to the building API
// (getOperationalLoop, @/lib/api/building.ts — GET .../equipment/{id}/operational-loop),
// already bucketed and gap-explicit server-side (services/building/.../domain/services/
// operational_loop.py) — see operational-loop-adapter.ts for the conversion into the
// `OperationalLoopPoint[]` shape below. An empty series from the API is unavailable, never a
// client-side demo curve.

export type OperationalLoopPoint = {
  key: number; // unique step index — the chart x-position
  time: string; // "HH:MM", Europe/Helsinki — display label only, never a chart dataKey (see `key`)
  isHourMark: boolean;
  isNow: boolean;
  isPast: boolean;

  // Real-sourced metrics are nullable: a bucket the API never reported for is a genuine gap
  // (no line drawn), never a fabricated 0.
  spotPriceCtPerKwh: number | null;
  outdoorTempC: number | null;
  solarRadiationWm2: number | null;

  occupancyIndex: number | null; // unused on real zones — see occupancyHeadcount/occupancyPresence.
  // Real zones report one of two shapes, never folded into each other (operational-loop-
  // adapter.ts `buildZonePoints` / `occupancyModeOf`): a zone with an `occupancy_count` sensor
  // gets its real headcount here (raw persons, no capacity math — the frontend never reads
  // `nominal_capacity_persons`); a zone with only an `occupancy` on/off signal gets presence
  // instead, drawn under its own "Läsnä" name/style, never dressed up as a 0–100 käyttöaste.
  occupancyHeadcount: number | null; // persons (real, from `occupancy_count`)
  occupancyPresence: number | null; // 0 | 100 (real, from `occupancy` on/off — headcount-less zones only)
  co2Index: number | null; // ppm
  // Zone room temperature (°C) — the third real zone signal a device's recipe actually reacts
  // to alongside occupancy/CO₂ (KB zone connection `zone_publishes`, e.g.
  // zones/connections/ahu-to-office.yaml: drives supply-temperature-limit calibration and
  // setback confirmation).
  zoneTemperatureC: number | null;

  fanPowerBaselinePct: number | null;
  fanPowerOptimizedPct: number | null;
  supplyTempBaselineC: number | null;
  supplyTempOptimizedC: number | null;
  // Mitattu — what the `baseline_of` sensor actually read after the recipe wrote the port
  // (verification question 1, ./verification.ts). History only: a future bucket
  // has nothing measured yet and stays null, never a forecast dressed up as a measurement.
  fanPowerMeasuredPct: number | null;
  supplyTempMeasuredC: number | null;

  elecKw: number | null;
  heatKw: number | null;
  totalOptimizedKw: number | null;
  totalBaselineKw: number | null;
  // kWh pair for the same fan-electrical term — only ever populated when the backend sent
  // `fan_electrical_kwh` (equipment's rated-power nameplate configured, task #1884/#1885).
  // `null` throughout means "stay on % of nameplate, no kWh axis", never a computed guess.
  elecKwh: number | null;
  totalBaselineKwh: number | null;
};

// Mirrors the fixed grid the backend defines (services/building/.../operational_loop.py).
// Real data always carries its own window in the API response.
export const STEP_MINUTES = 15;
export const PAST_STEPS = 96; // 24 h history
export const FUTURE_STEPS = 96; // 24 h forecast — fixed -24h..+24h window
export const HELSINKI_TIME_ZONE = "Europe/Helsinki";

export type ForecastSplitRow = {
  key: number;
  time: string;
  isHourMark: boolean;
  [field: string]: string | boolean | number | null;
};

// Recharts needs a distinct dataKey per stroke style, so each metric is split into a
// "…Past" (solid, history) and "…Future" (dashed, forecast) key — both populated at
// isNow so the two segments join up without a visible gap. A `null` source value (no real
// data for this bucket) stays `null` in both halves — Recharts breaks the line there instead
// of drawing through a fabricated point.
export function splitForecast<K extends keyof OperationalLoopPoint>(
  points: OperationalLoopPoint[],
  keys: readonly K[],
): ForecastSplitRow[] {
  return points.map((p) => {
    const row: ForecastSplitRow = {
      key: p.key,
      time: p.time,
      isHourMark: p.isHourMark,
    };
    for (const key of keys) {
      const value = p[key] as number | null;
      row[`${String(key)}Past`] = p.isPast ? value : null;
      row[`${String(key)}Future`] = !p.isPast || p.isNow ? value : null;
    }
    return row;
  });
}

// The single "now" x-position every chart's NowMarker aligns to.
export function nowKeyOf(points: OperationalLoopPoint[]): number {
  return points.find((p) => p.isNow)?.key ?? points[0]?.key ?? 0;
}
